import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CloseIcon from '@mui/icons-material/Close'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useNavigate } from 'react-router-dom'
import LogoShort from 'assets/images/bp-logo-orange.png'
import {
  selectGuruWidgetOpen,
  selectGuruWidgetView,
  selectGuruWidgetPosition,
} from 'ducks/guruWidget/selectors'
import { selectPortfolioList, selectSelectedPortfolio } from 'ducks/portfolio/selectors'
import { listPortfoliosRoutine } from 'ducks/portfolio/actions'
import { selectEstates } from 'ducks/estates/selectors'
import { listEstatesRoutine } from 'ducks/estates/actions'
import { getUserBps } from 'features/bpList/ducks/selectors'
import { fetchUserBpsRoutine } from 'features/bpList/ducks/actions'
import { closeWidget, setView, setPosition, resetPosition } from 'ducks/guruWidget/actions'
import { clearChatRoutine } from 'ducks/guruChat/actions'
import GuruSuggestions from './GuruSuggestions'
import GuruChat from 'features/bpDetails/Components/guru/GuruChat'
import { useGuruScope } from './hooks/useGuruScope'
import GuruScopeChip from './GuruScopeChip'

const DEFAULT_RIGHT = 24
const DEFAULT_BOTTOM = 96
const PANEL_WIDTH = 380
const PANEL_HEIGHT = 600
const EDGE_MARGIN = 8
const SWIPE_DOWN_THRESHOLD = 80

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1299;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`

const Panel = styled.div`
  position: fixed;
  right: ${({ $right }) => $right}px;
  bottom: ${({ $bottom }) => $bottom}px;
  width: ${PANEL_WIDTH}px;
  height: ${PANEL_HEIGHT}px;
  max-height: calc(100vh - 120px);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  z-index: 1300;
  overflow: hidden;
  transform: translateY(${({ $sheetDragY }) => $sheetDragY || 0}px);
  transition: ${({ $dragging, $sheetDragY }) =>
    $dragging || $sheetDragY ? 'none' : 'transform 0.2s ease-out, box-shadow 0.15s'};

  @media (max-width: 768px) {
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 90vh;
    max-height: 90vh;
    border-radius: 14px 14px 0 0;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  cursor: ${({ $draggable }) => ($draggable ? 'grab' : 'default')};
  user-select: none;
  touch-action: none;

  &:active {
    cursor: ${({ $draggable }) => ($draggable ? 'grabbing' : 'default')};
  }

  @media (max-width: 768px) {
    cursor: default;
  }
`

const MobileGrabber = styled.div`
  display: none;
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.lightGrey};
  margin: 6px auto 0;

  @media (max-width: 768px) {
    display: block;
  }
`

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-right: 2px;

  @media (max-width: 768px) {
    display: none;
  }
`

const HeaderAvatar = styled.img`
  width: 24px !important;
  height: 24px !important;
  min-width: 24px;
  min-height: 24px;
  max-width: 24px;
  max-height: 24px;
  object-fit: contain;
`

const HeaderTitle = styled.div`
  flex: 1;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const Tab = styled.button`
  flex: 1;
  background: transparent;
  border: none;
  padding: 10px 0;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: ${({ theme, $active }) => $active ? theme.colors.haiti : theme.colors.grey[200]};
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.colors.primary : 'transparent'};
  transition: all 0.15s;
`

const ChatHost = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches

// Lookup the portfolio matching the current widget context, prefer the
// already-selected portfolio (richer data, includes BPs) and fall back to
// the cached portfolio list. Returns null when not in portfolio context.
function useMemoPortfolio(ctx, selectedPortfolio, portfolioList) {
  return useMemo(() => {
    if (ctx?.contextType !== 'portfolio' || !ctx?.contextId) return null
    if (selectedPortfolio?.id === ctx.contextId) return selectedPortfolio
    const list = Array.isArray(portfolioList) ? portfolioList : []
    return list.find((p) => p?.id === ctx.contextId) || null
  }, [ctx, selectedPortfolio, portfolioList])
}

function useMemoEstate(ctx, estateList) {
  return useMemo(() => {
    if (ctx?.contextType !== 'estate' || !ctx?.contextId) return null
    const list = Array.isArray(estateList) ? estateList : []
    return list.find((e) => e?.id === ctx.contextId) || null
  }, [ctx, estateList])
}

const clampPosition = (right, bottom) => {
  const maxRight = Math.max(EDGE_MARGIN, window.innerWidth - PANEL_WIDTH - EDGE_MARGIN)
  const maxBottom = Math.max(EDGE_MARGIN, window.innerHeight - PANEL_HEIGHT - EDGE_MARGIN)
  return {
    right: Math.min(Math.max(EDGE_MARGIN, right), maxRight),
    bottom: Math.min(Math.max(EDGE_MARGIN, bottom), maxBottom),
  }
}

const GuruWidget = () => {
  const open = useSelector(selectGuruWidgetOpen)
  const view = useSelector(selectGuruWidgetView)
  const persistedPosition = useSelector(selectGuruWidgetPosition)
  const selectedPortfolio = useSelector(selectSelectedPortfolio)
  const portfolioList = useSelector(selectPortfolioList)
  const estateList = useSelector(selectEstates)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { scope: ctx, setScope } = useGuruScope()

  const portfolioForCtx = useMemoPortfolio(ctx, selectedPortfolio, portfolioList)
  const estateForCtx = useMemoEstate(ctx, estateList)
  const bpList = useSelector(getUserBps)

  useEffect(() => {
    if (!open) return
    if (!Array.isArray(bpList) || bpList.length === 0) {
      dispatch(fetchUserBpsRoutine({ page: 1 }))
    }
    if (!Array.isArray(portfolioList) || portfolioList.length === 0) {
      dispatch(listPortfoliosRoutine())
    }
    if (!Array.isArray(estateList) || estateList.length === 0) {
      dispatch(listEstatesRoutine({ page: 1 }))
    }
  }, [open, dispatch])

  const [dragging, setDragging] = useState(false)
  const [sheetDragY, setSheetDragY] = useState(0)
  const dragStateRef = useRef(null)
  const sheetStartYRef = useRef(null)

  const right = persistedPosition?.right ?? DEFAULT_RIGHT
  const bottom = persistedPosition?.bottom ?? DEFAULT_BOTTOM

  // Desktop drag-to-move from the header
  const handleHeaderMouseDown = (e) => {
    if (isMobileViewport()) return
    if (e.button !== 0) return
    if (e.target.closest('button')) return

    dragStateRef.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startRight: right,
      startBottom: bottom,
    }
    setDragging(true)
  }

  useEffect(() => {
    if (!dragging) return undefined

    const onMove = (e) => {
      const s = dragStateRef.current
      if (!s) return
      const deltaX = e.clientX - s.startMouseX
      const deltaY = e.clientY - s.startMouseY
      const next = clampPosition(s.startRight - deltaX, s.startBottom - deltaY)
      dispatch(setPosition(next))
    }
    const onUp = () => {
      setDragging(false)
      dragStateRef.current = null
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dragging, dispatch])

  // Mobile swipe-down on the grabber
  const handleSheetTouchStart = (e) => {
    if (!isMobileViewport()) return
    sheetStartYRef.current = e.touches[0].clientY
  }
  const handleSheetTouchMove = (e) => {
    if (sheetStartYRef.current == null) return
    const delta = e.touches[0].clientY - sheetStartYRef.current
    if (delta > 0) setSheetDragY(delta)
  }
  const handleSheetTouchEnd = () => {
    if (sheetStartYRef.current == null) return
    if (sheetDragY > SWIPE_DOWN_THRESHOLD) {
      setSheetDragY(0)
      sheetStartYRef.current = null
      dispatch(closeWidget())
      return
    }
    setSheetDragY(0)
    sheetStartYRef.current = null
  }

  // Reposition if the viewport shrinks below the persisted offsets
  useEffect(() => {
    if (!persistedPosition) return undefined
    const onResize = () => {
      const next = clampPosition(persistedPosition.right, persistedPosition.bottom)
      if (next.right !== persistedPosition.right || next.bottom !== persistedPosition.bottom) {
        dispatch(setPosition(next))
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [persistedPosition, dispatch])

  if (!open) return null

  const handleExpand = () => {
    if (ctx.contextType === 'bp' && ctx.contextId) {
      navigate(`/bp-list/${ctx.contextId}/guru`)
      dispatch(closeWidget())
    } else if (ctx.contextType === 'portfolio' && ctx.contextId) {
      // PortfolioDetail uses ?tab=guru on the same /portfolios/:id route — a
      // /guru sub-segment 404s because no such route is registered.
      navigate(`/portfolios/${ctx.contextId}?tab=guru`)
      dispatch(closeWidget())
    } else if (ctx.contextType === 'estate' && ctx.contextId) {
      navigate(`/estates/${ctx.contextId}?tab=guru`)
      dispatch(closeWidget())
    } else if (ctx.contextType === 'user') {
      navigate('/guru')
      dispatch(closeWidget())
    }
  }

  const content = (
    <>
      <Backdrop onClick={() => dispatch(closeWidget())} />
      <Panel
        role="dialog"
        aria-label="Guru assistant"
        $right={right}
        $bottom={bottom}
        $dragging={dragging}
        $sheetDragY={sheetDragY}
      >
        <MobileGrabber
          onTouchStart={handleSheetTouchStart}
          onTouchMove={handleSheetTouchMove}
          onTouchEnd={handleSheetTouchEnd}
        />
        <Header
          $draggable
          onMouseDown={handleHeaderMouseDown}
          onDoubleClick={() => dispatch(resetPosition())}
        >
          <DragHandle aria-hidden>
            <DragIndicatorIcon style={{ fontSize: 16 }} />
          </DragHandle>
          <HeaderAvatar src={LogoShort} alt="Guru" />
          <HeaderTitle>Building Passport Guru</HeaderTitle>
          {persistedPosition && (
            <Tooltip title="Reset position">
              <IconButton size="small" onClick={() => dispatch(resetPosition())} aria-label="Reset position">
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {view === 'chat' && (
            <Tooltip title="Clear chat">
              <IconButton size="small" onClick={() => dispatch(clearChatRoutine())} aria-label="Clear chat">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {ctx.contextType && (
            <Tooltip title="Expand to full page">
              <IconButton size="small" onClick={handleExpand} aria-label="Expand to full page">
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close (Esc)">
            <IconButton size="small" onClick={() => dispatch(closeWidget())} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Header>
        <GuruScopeChip scope={ctx} onSelect={setScope} />
        <Tabs>
          <Tab $active={view === 'suggestions'} onClick={() => dispatch(setView('suggestions'))}>
            Suggestions
          </Tab>
          <Tab $active={view === 'chat'} onClick={() => dispatch(setView('chat'))}>
            Chat
          </Tab>
        </Tabs>
        {view === 'suggestions' ? (
          <GuruSuggestions />
        ) : (
          <ChatHost>
            <GuruChat
              mode="widget"
              bpId={ctx.contextType === 'bp' ? ctx.contextId : null}
              portfolioId={ctx.contextType === 'portfolio' ? ctx.contextId : null}
              portfolioName={portfolioForCtx?.name || null}
              estateId={ctx.contextType === 'estate' ? ctx.contextId : null}
              estateName={estateForCtx?.name || null}
              buildings={estateForCtx?.buildingPassports || null}
              userMode={ctx.contextType === 'user'}
              buildingCount={
                Number.isFinite(portfolioForCtx?.buildingPassportsCount)
                  ? portfolioForCtx.buildingPassportsCount
                  : Array.isArray(portfolioForCtx?.buildingPassports)
                    ? portfolioForCtx.buildingPassports.length
                    : Number.isFinite(estateForCtx?.bpCount)
                      ? estateForCtx.bpCount
                      : Array.isArray(estateForCtx?.buildingPassports)
                        ? estateForCtx.buildingPassports.length
                        : null
              }
            />
          </ChatHost>
        )}
      </Panel>
    </>
  )

  return ReactDOM.createPortal(content, document.body)
}

export default GuruWidget
